/**
 * useBoardColumns — Fetches real column configuration from Jira board.
 *
 * Calls `agile-get-board-columns` to get the actual column layout.
 * Falls back gracefully if the endpoint fails or boardId is not available.
 *
 * Returns:
 * - columns: Array of { name, statusNames[] } representing real board columns
 * - loading: Whether the fetch is in progress
 * - isFromJira: true if columns came from Jira, false if inferred
 * - error: Error message if fetch failed
 */

import { useEffect, useState, useRef } from 'react'
import { runTool } from '../services/toolRunner'
export interface BoardColumn {
  name: string
  /** Status names that map to this column (from Jira config) */
  statusNames: string[]
}
export interface BoardColumnsResult {
  columns: BoardColumn[] | null
  loading: boolean
  isFromJira: boolean
  error: string | null
}

// Simple in-memory cache to avoid re-fetching on every render
const columnsCache = new Map<number, BoardColumn[]>()

/**
 * Parse the response from agile-get-board-columns or agile-get-board-configuration.
 * Handles multiple possible response shapes from Jira API.
 */
function parseColumnsResponse(data: any): BoardColumn[] | null {
  // Shape 1: { columns: [{ name, statuses: [{ id, self }] }] }
  // Shape 2: { columnConfig: { columns: [...] } }
  // Shape 3: Direct array of columns

  let rawColumns: any[] | null = null
  if (Array.isArray(data?.columns)) {
    rawColumns = data.columns
  } else if (Array.isArray(data?.columnConfig?.columns)) {
    rawColumns = data.columnConfig.columns
  } else if (Array.isArray(data)) {
    rawColumns = data
  }
  if (!rawColumns || rawColumns.length === 0) return null
  const parsed: BoardColumn[] = rawColumns.map((col: any) => {
    const name = col.name || col.label || 'Sin nombre'
    const statuses = Array.isArray(col.statuses) ? col.statuses : []
    const statusNames = statuses
      .map((s: any) => s.name || s.id || '')
      .filter(Boolean)
    return {
      name,
      statusNames,
    }
  })

  // Filter out empty columns (no name)
  return parsed.filter((c) => c.name)
}
export function useBoardColumns(boardId: number | null): BoardColumnsResult {
  const [columns, setColumns] = useState<BoardColumn[] | null>(null)
  const [loading, setLoading] = useState(false)
  const [isFromJira, setIsFromJira] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const fetchedRef = useRef<number | null>(null)
  useEffect(() => {
    if (!boardId || boardId === fetchedRef.current) return

    // Check cache first
    const cached = columnsCache.get(boardId)
    if (cached) {
      setColumns(cached)
      setIsFromJira(true)
      setLoading(false)
      setError(null)
      fetchedRef.current = boardId
      return
    }
    let cancelled = false
    const fetchColumns = async () => {
      setLoading(true)
      setError(null)
      try {
        // Try agile-get-board-columns first (lighter endpoint)
        const result = await runTool('agile-get-board-columns', {
          boardId,
        })
        if (cancelled) return
        if (result.success && result.data) {
          const parsed = parseColumnsResponse(result.data)
          if (parsed && parsed.length > 0) {
            columnsCache.set(boardId, parsed)
            setColumns(parsed)
            setIsFromJira(true)
            fetchedRef.current = boardId
            console.log(
              `[useBoardColumns] ✓ Got ${parsed.length} columns from Jira for board ${boardId}:`,
              parsed.map((c) => c.name).join(', '),
            )
            setLoading(false)
            return
          }
        }

        // Fallback: try agile-get-board-configuration (heavier but more complete)
        console.log(
          `[useBoardColumns] agile-get-board-columns failed/empty, trying configuration...`,
        )
        const configResult = await runTool('agile-get-board-configuration', {
          boardId,
        })
        if (cancelled) return
        if (configResult.success && configResult.data) {
          const parsed = parseColumnsResponse(configResult.data)
          if (parsed && parsed.length > 0) {
            columnsCache.set(boardId, parsed)
            setColumns(parsed)
            setIsFromJira(true)
            fetchedRef.current = boardId
            console.log(
              `[useBoardColumns] ✓ Got ${parsed.length} columns from board config for board ${boardId}`,
            )
            setLoading(false)
            return
          }
        }

        // Both failed — fallback to null (consumer will infer from issues)
        console.warn(
          `[useBoardColumns] Could not get columns for board ${boardId}, will use fallback`,
        )
        setColumns(null)
        setIsFromJira(false)
        setError('No se pudieron obtener las columnas del board')
        fetchedRef.current = boardId
      } catch (err: any) {
        if (cancelled) return
        console.error('[useBoardColumns] Error:', err)
        setColumns(null)
        setIsFromJira(false)
        setError(err.message || 'Error desconocido')
        fetchedRef.current = boardId
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    fetchColumns()
    return () => {
      cancelled = true
    }
  }, [boardId])
  return {
    columns,
    loading,
    isFromJira,
    error,
  }
}

/**
 * Helper: Given an issue status and board columns, find which column it belongs to.
 * Returns the column name, or null if no match found.
 */
export function findColumnForStatus(
  status: string,
  columns: BoardColumn[],
): string | null {
  const statusLower = status.toLowerCase()
  for (const col of columns) {
    if (col.statusNames.some((s) => s.toLowerCase() === statusLower)) {
      return col.name
    }
  }
  return null
}

/** Clear the columns cache (useful on refresh) */
export function clearBoardColumnsCache() {
  columnsCache.clear()
}
